
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';

interface EventCardProps {
  event: string;
  delay: number;
  imageUrl: string;
  eventType: string;
}

const EventCard: React.FC<EventCardProps> = ({ event, delay, imageUrl, eventType }) => {
  return (
    <div 
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden animate-on-scroll"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Added event image */}
      <div className="h-40 overflow-hidden">
        <img 
          src={imageUrl} 
          alt={eventType} 
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="p-6">
        <div className="flex items-center mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500/30 to-indigo-500/30 rounded-full flex items-center justify-center mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 px-3 py-1 rounded-full">
            <span className="text-sm text-purple-300">{eventType}</span>
          </div>
        </div>
        <h3 className="text-white font-medium mb-2">{event}</h3>
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-sm text-gray-400">Online / Virtual</span>
        </div>
      </div>
    </div>
  );
};

const EventsSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  
  // Event data with images
  const events = [
    {
      event: t('events.event1'),
      delay: 0,
      imageUrl: '/background_images/events_event1.jpg',
      eventType: 'Webinar'
    },
    {
      event: t('events.event2'),
      delay: 100,
      imageUrl: '/background_images/events_event2.jpg',
      eventType: 'Meetup'
    },
    {
      event: t('events.event3'),
      delay: 200,
      imageUrl: '/background_images/events_event3.jpg',
      eventType: 'In-person Event'
    }
  ];
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative bg-gradient-to-b from-gray-900 to-indigo-950/30">
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/background_images/events_event3.jpg')] bg-cover bg-center opacity-5"></div>
      <div className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full bg-purple-500/5 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll">
            {t('events.header')}
          </h2>
          
          <p className="text-3xl md:text-3x1 text-purple-400 mb-6 animate-on-scroll">
            {t('events.subtitle')}
          </p>
          
          <p className="text-lg text-gray-300 max-w-3xl mx-auto animate-on-scroll">
            {t('events.text')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {events.map((eventItem, index) => (
            <EventCard 
              key={index}
              event={eventItem.event}
              delay={eventItem.delay}
              imageUrl={eventItem.imageUrl}
              eventType={eventItem.eventType}
            />
          ))}
        </div>
        
        {/* Community highlights */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 animate-on-scroll">
            <h3 className="text-xl font-semibold mb-4 text-white">{t('events.communityHighlights')}</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500/30 to-indigo-500/30 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium">{t('events.weeklyStrategySessions')}</h4>
                  <p className="text-sm text-gray-300">{t('events.weeklyStrategySessionsDesc')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500/30 to-indigo-500/30 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium">{t('events.investorInterviews')}</h4>
                  <p className="text-sm text-gray-300">{t('events.investorInterviewsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 animate-on-scroll" style={{ animationDelay: '100ms' }}>
            <h3 className="text-xl font-semibold mb-4 text-white">{t('events.educationalResources')}</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500/30 to-indigo-500/30 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium">{t('events.tradingFundamentals')}</h4>
                  <p className="text-sm text-gray-300">{t('events.tradingFundamentalsDesc')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500/30 to-indigo-500/30 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-white font-medium">{t('events.expertWebinars')}</h4>
                  <p className="text-sm text-gray-300">{t('events.expertWebinarsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
